import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardActions,
  Button,
  LinearProgress,
  Alert,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Completion Icon
import { fetchUserData } from '../config/firestoreConfig';
import { useAuth } from '../context/AuthContext'; // Assuming you have an AuthContext
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const EnrolledCoursesPage = () => {
  const { user } = useAuth(); // Get the logged-in user
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check if it's mobile size
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // Check if it's tablet size

  useEffect(() => {
    const getEnrolledCourses = async () => {
      if (!user) return; // If no user, exit

      try {
        setIsLoading(true);
        const userData = await fetchUserData(user.email);
        if (userData && userData.enrolled) {
          const courses = Object.values(userData.enrolled); // Convert enrolled courses object to an array
          setEnrolledCourses(courses);
        } else {
          setEnrolledCourses([]); // No enrolled courses
        }
      } catch (err) {
        setError("Failed to fetch enrolled courses. Please try again later.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    getEnrolledCourses();
  }, [user]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 8,
        mt: isMobile ? 4 : isTablet ? 6 : 8,
      }}
    >
      <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 6, color: '#000' }}>
        My Enrolled Courses
      </Typography>

      {enrolledCourses.length === 0 ? (
        <Typography variant="h6" color="textSecondary" align="center">
          You have not enrolled in any courses yet. Browse our <Link to="/courses">Available Courses</Link>.
        </Typography>
      ) : (
        <Grid container spacing={4}>
          {enrolledCourses.map((course) => (
            <Grid item key={course.id} xs={12} sm={6} md={4}>
              <Card
                sx={{
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': { boxShadow: 6, transform: 'scale(1.05)' },
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                }}
              >
                <CardActionArea component={Link} to={`/courses/${course.id}/content`}>
                  <CardContent sx={{ padding: 3, position: 'relative' }}>
                    {course.progress === 100 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          backgroundColor: '#4caf50',
                          color: '#fff',
                          borderRadius: '50%',
                          padding: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircleIcon />
                      </Box>
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000', mb: 1 }}>
                      {course.title || `Course ID: ${course.id}`} {/* Fallback if title not available */}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ color: '#555', lineHeight: 1.6 }}>
                        Enrolled on: {course.date ? new Date(course.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }) : 'Unknown'}
                    </Typography>

                    <Typography variant="body2" color="textSecondary" sx={{ color: '#555', lineHeight: 1.6, mt: 1 }}>
                      {course.progress === 100
                        ? 'Completed'
                        : `Progress: ${Math.floor(course.progress || 0)}%`}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={course.progress || 0}
                      sx={{
                        mt: 2,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': { backgroundColor: '#000' },
                      }}
                    />
                  </CardContent>
                </CardActionArea>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to={`/courses/${course.id}/content`}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 'bold',
                      backgroundColor: '#000', 
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#fff', 
                        color: '#000', 
                        border: '1px solid #000', 
                      },
                    }}
                  >
                    Open Course
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default EnrolledCoursesPage;
